import React, { useCallback } from 'react';
import { fromJS, List } from 'immutable';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { segmentTrack } from 'flow/actions/segment-actions';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';
import { MessageContent } from 'flow/components/tickets/UseAttachmentHandler';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { PickedMention } from 'flow/types/Tickets';
import { TypedMap } from 'flow/types/TypedMap';
import { shiftPickedMentions } from 'flow/utils/ticket-utils';

interface UseMentionAutoCompleteParams {
    messageContentInternal: MessageContent;
    setMessageContentInternal: React.Dispatch<
        React.SetStateAction<MessageContent>
    >;
    getCaretPosition: () => number | undefined;
    setCaretPosition: (position: number) => void;
    setShowMentionPicker: (show: boolean) => void;
    sendMessage: () => void | Promise<void>;
}

export const useMentionAutoComplete = ({
    messageContentInternal,
    setMessageContentInternal,
    getCaretPosition,
    setCaretPosition,
    setShowMentionPicker,
    sendMessage,
}: UseMentionAutoCompleteParams) => {
    const dispatch = useAppDispatch();

    const handleMentionAutoComplete = useCallback(
        async (name: string, extensionKey?: ExtensionKey) => {
            // If no name provided, send the message instead
            if (!name) {
                await sendMessage();
                return;
            }

            const mentionText = `@${name} `;
            const trimCharacter = '@';

            const caretPosition = getCaretPosition();
            const messageText = messageContentInternal?.text || '';

            // Split text at caret position
            const textBeforeCaret = messageText.substring(0, caretPosition);
            const textAfterCaret = messageText.substring(caretPosition);

            // Remove the partial mention text (everything after the last @)
            const trimmedTextBeforeCaret = textBeforeCaret.substring(
                0,
                textBeforeCaret.lastIndexOf(trimCharacter),
            );

            // Construct new text with completed mention
            const newText =
                trimmedTextBeforeCaret + mentionText + textAfterCaret;

            // Update message content
            const mentionStart = trimmedTextBeforeCaret.length;
            setMessageContentInternal((prev) => {
                const mentionsShifted = shiftPickedMentions(
                    prev.mentions,
                    prev.text,
                    newText,
                    mentionStart + mentionText.length,
                );
                if (!extensionKey) {
                    return {
                        ...prev,
                        text: newText,
                        mentions: mentionsShifted,
                    };
                }

                const pickedMention: TypedMap<PickedMention> = fromJS({
                    extensionKey,
                    name,
                    start: mentionStart,
                });
                return {
                    ...prev,
                    text: newText,
                    mentions: (
                        mentionsShifted ?? List<TypedMap<PickedMention>>()
                    ).push(pickedMention),
                };
            });

            // Calculate new caret position
            const lengthDiff =
                mentionText.length -
                (textBeforeCaret.length - trimmedTextBeforeCaret.length);
            setCaretPosition(caretPosition + lengthDiff);

            // Close mention picker and track the event
            setShowMentionPicker(false);
            dispatch(segmentTrack(TICKET_STRINGS.USER_MENTIONED_COLLEAGUE));
        },
        [
            messageContentInternal,
            setMessageContentInternal,
            getCaretPosition,
            setCaretPosition,
            setShowMentionPicker,
            sendMessage,
            dispatch,
        ],
    );

    return { handleMentionAutoComplete };
};
